import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { generateColorMap, DEFAULT_CHART_COLOR } from './colorPalettes';
import { getChartColors, getChartTitle, getChartAxis, getChartLegend, getChartFont } from '../../utils/chartColors';
import './chartTitles.css';

/**
 * Reusable Area Chart Component
 * @param {Array} data - Array of data objects with x and y values
 * @param {string} xKey - Key for x-axis data
 * @param {string} yKey - Key for y-axis data
 * @param {string} groupKey - Optional key for multi-series grouping (e.g., 'ticker')
 * @param {string} title - Chart title
 * @param {string} xLabel - X-axis label
 * @param {string} yLabel - Y-axis label
 * @param {string} color - Fill color (single series only)
 * @param {boolean} formatCurrency - Add $ prefix to y-axis
 * @param {number} opacity - Fill opacity (0-1)
 * @param {boolean} stackGroup - Stack multiple series
 */
const AreaChart = ({ 
  data = [], 
  xKey = 'date', 
  yKey = 'value',
  groupKey = null,
  title = 'Area Chart',
  xLabel = 'Date',
  yLabel = 'Value',
  color = DEFAULT_CHART_COLOR,
  formatCurrency = false,
  opacity = 0.55,
  stackGroup = false
}) => {
  const [themeKey, setThemeKey] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Listen for theme changes and window resize
  useEffect(() => {
    const checkThemeChange = () => {
      setThemeKey(prev => prev + 1);
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setThemeKey(prev => prev + 1); // Force re-render on resize
    };

    // Listen for DOM mutations (theme class changes)
    const observer = new MutationObserver(checkThemeChange);
    const landingPage = document.querySelector('.landing-page');
    if (landingPage) {
      observer.observe(landingPage, { attributes: true, attributeFilter: ['class'] });
    }

    // Listen for window resize
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  let plotData = [];

  if (groupKey && data.length > 0 && data[0][groupKey]) {
    // Multi-series support
    const groups = [...new Set(data.map(item => item[groupKey]))];
    const colorMap = generateColorMap(groups);

    groups.forEach(group => {
      const groupData = data.filter(item => item[groupKey] === group);
      const groupColor = colorMap[group] || DEFAULT_CHART_COLOR;
      
      plotData.push({
        x: groupData.map(item => item[xKey]),
        y: groupData.map(item => item[yKey]),
        name: group,
        type: 'scatter',
        mode: 'lines',
        connectgaps: true,
        fill: stackGroup ? 'tonexty' : 'tozeroy',
        fillcolor: `${groupColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`,
        line: {
          color: groupColor,
          width: 2
        },
        stackgroup: stackGroup ? 'one' : null,
        hovertemplate: `<b>${group}</b><br>${yLabel}: %{y}<br>${xLabel}: %{x}<extra></extra>`
      });
    });
  } else {
    // Single series
    plotData = [{
      x: data.map(item => item[xKey]),
      y: data.map(item => item[yKey]),
      type: 'scatter',
      mode: 'lines',
      connectgaps: true,
      fill: 'tozeroy',
      fillcolor: `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`,
      line: {
        color: color,
        width: 2
      },
      hovertemplate: `<b>${yLabel}</b>: %{y}<br><b>${xLabel}</b>: %{x}<extra></extra>`,
      showlegend: false
    }];
  }

  // Sort plotData alphabetically by name for consistent legend ordering
  if (plotData.length > 1 && plotData.some(trace => trace.name)) {
    plotData.sort((a, b) => {
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    });
  }

  const colors = getChartColors();
  const isDarkMode = !document.querySelector('.landing-page')?.classList.contains('light-mode');
  
  // Responsive font sizes
  let tickSize = 12;
  if (windowWidth <= 480) {
    tickSize = 9;
  } else if (windowWidth <= 768) {
    tickSize = 10;
  } else if (windowWidth <= 1024) {
    tickSize = 11;
  }

  const xAxisConfig = getChartAxis(xLabel, true);
  const yAxisConfig = getChartAxis(yLabel, false);
  
  // Add responsive tick font and currency prefix to axes
  xAxisConfig.tickfont = getChartFont(tickSize);
  yAxisConfig.tickfont = getChartFont(tickSize);
  yAxisConfig.tickprefix = formatCurrency ? '$' : '';
  if (yLabel) {
    yAxisConfig.title.standoff = 20;
  }

  const itemCount = groupKey && data.length > 0 && data[0][groupKey] 
    ? [...new Set(data.map(item => item[groupKey]))].length 
    : 0;

  const titleConfig = getChartTitle(title, 20);
  // Override title positioning to match original (x: 0, xanchor: 'left')
  titleConfig.x = 0;
  titleConfig.xanchor = 'left';
  titleConfig.y = undefined;
  titleConfig.yanchor = undefined;

  const layout = {
    title: titleConfig,
    paper_bgcolor: colors.background,
    plot_bgcolor: isDarkMode ? 'rgba(15,15,15,0.5)' : 'rgba(0,0,0,0)',
    xaxis: xAxisConfig,
    yaxis: yAxisConfig,
    margin: { 
      t: 80, 
      r: windowWidth <= 768 ? 20 : 40, 
      b: (() => {
        const isMobile = windowWidth <= 768;
        return isMobile ? (itemCount > 6 ? 140 : 120) : 40;
      })(), 
      l: 70 
    },
    hovermode: 'closest',
    legend: itemCount > 0 ? getChartLegend(itemCount, windowWidth) : {
      font: getChartFont(12),
      bgcolor: 'rgba(0,0,0,0)'
    },
    autosize: true
  };

  const config = {
    responsive: true,
    displayModeBar: false
  };

  return (
    <Plot
      key={themeKey}
      data={plotData}
      layout={layout}
      config={config}
      style={{ width: '100%', height: '100%' }}
      useResizeHandler={true}
    />
  );
};

export default AreaChart;


